import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-multi-select-dropdown',
  standalone: true,
  imports: [FormsModule, TranslatePipe],
  templateUrl: './multi-select-dropdown.component.html',
  styleUrl: './multi-select-dropdown.component.scss',
})
export class MultiSelectDropdownComponent implements OnInit {
  @Input() label: string = '';
  @Input() options: { label: string; value: string }[] = [];
  @Input() multiple: boolean = true;
  @Input() selected: string | string[] = [];
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = false;
  @Output() selectedChange = new EventEmitter<string[] | string>();
  @Output() searchSubmitted = new EventEmitter<string>();
  dropdownOpen = false;
  searchTerm: string = '';
  protected readonly Array = Array;
  protected searchTerm$ = new Subject<string>();

  ngOnInit() {
    if (this.multiple && !Array.isArray(this.selected)) {
      this.selected = this.selected ? [this.selected as string] : [];
    } else if (!this.multiple && Array.isArray(this.selected)) {
      this.selected = this.selected.length > 0 ? this.selected[0] : '';
    }

    this.searchTerm$.pipe(debounceTime(300)).subscribe((value) => {
      const trimmed = value.trim();
      this.searchSubmitted.emit(trimmed);
    });
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.dropdownOpen = !this.dropdownOpen;
    }
  }

  isSelected(option: string): boolean {
    if (this.multiple && !Array.isArray(this.selected)) {
      this.selected = this.selected ? [this.selected as string] : [];
    }
    return Array.isArray(this.selected)
      ? this.selected.includes(option)
      : this.selected === option;
  }

  getOptionLabel(value: string): string {
    return this.options.find((o) => o.value === value)?.label ?? '';
  }

  isSearchMode(): boolean {
    return this.searchable;
  }

  getDisplayText(): string {
    if (this.multiple && Array.isArray(this.selected)) {
      return this.selected.length > 0
        ? `${this.selected.length} selected`
        : this.placeholder;
    } else if (
      !this.multiple &&
      !Array.isArray(this.selected) &&
      this.selected
    ) {
      return this.getOptionLabel(this.selected as string);
    }
    return this.placeholder;
  }

  onSearchChange(value: string) {
    this.searchTerm$.next(value);
  }

  isMultipleMode(): boolean {
    return this.multiple;
  }

  isSingleMode(): boolean {
    return !this.multiple;
  }

  hasSelectedItems(): boolean {
    if (this.multiple) {
      return Array.isArray(this.selected) && this.selected.length > 0;
    } else {
      return !Array.isArray(this.selected) && !!this.selected;
    }
  }

  getSelectedCount(): number {
    return Array.isArray(this.selected) ? this.selected.length : 0;
  }

  getSingleSelectedLabel(): string {
    return !Array.isArray(this.selected) && this.selected
      ? this.getOptionLabel(this.selected as string)
      : '';
  }

  onOptionToggle(option: string) {
    if (this.disabled) return;

    if (this.multiple) {
      const selectedArray = Array.isArray(this.selected)
        ? [...this.selected]
        : [];
      const updated = selectedArray.includes(option)
        ? selectedArray.filter((o) => o !== option)
        : [...selectedArray, option];

      this.selected = updated;
      this.selectedChange.emit(updated);
    } else {
      this.selected = this.selected === option ? '' : option;
      this.selectedChange.emit(this.selected);
      if (this.selected) {
        this.dropdownOpen = false;
      }
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    } else if (event.key === 'Escape') {
      this.dropdownOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  closeDropdown(event: Event) {
    if (!(event.target as HTMLElement).closest('.dropdown-wrapper')) {
      this.dropdownOpen = false;
    }
  }
}
